"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModalProvider = exports.modalContext = void 0;
const react_1 = __importDefault(require("react"));
const is_defined_1 = require("../is-defined");
exports.modalContext = react_1.default.createContext(undefined);
const createSingleLayer = (modal) => ({
    mode: 'single',
    modals: [modal],
});
const ModalProvider = ({ children }) => {
    const [layers, setLayers] = react_1.default.useState([]);
    const createModal = react_1.default.useCallback((modal) => {
        setLayers((prev) => [...prev, createSingleLayer(modal)]);
    }, [setLayers]);
    const popLayer = react_1.default.useCallback(() => {
        setLayers((prev) => {
            const newLayers = [...prev];
            newLayers.pop();
            return newLayers;
        });
    }, [setLayers]);
    const createLayer = react_1.default.useCallback((layer) => {
        setLayers((prev) => [...prev, layer]);
    }, [setLayers]);
    const addModal = react_1.default.useCallback((modal) => {
        setLayers((prev) => {
            const _temp = prev.at(-1);
            if ((0, is_defined_1.isUndefined)(_temp)) {
                // Nothing to do, do nothing.
                return prev;
            }
            const layer = Object.assign({}, _temp);
            layer.modals = Array.from(_temp.modals);
            layer.mode = 'all';
            layer.modals.push(modal);
            return [...prev.slice(0, -1), layer];
        });
    }, [setLayers]);
    const replaceModal = react_1.default.useCallback((modal) => {
        setLayers((prev) => {
            const lastLayer = prev.at(-1);
            if ((0, is_defined_1.isUndefined)(lastLayer)) {
                return prev;
            }
            const layer = { ...lastLayer, modals: [...lastLayer.modals] };
            if (layer.modals.length > 0) {
                layer.modals[layer.modals.length - 1] = modal;
            }
            else {
                layer.modals.push(modal);
            }
            return [...prev.slice(0, -1), layer];
        });
    }, [setLayers]);
    const popModal = react_1.default.useCallback(() => {
        setLayers((prev) => {
            const _temp = prev.at(-1);
            if ((0, is_defined_1.isUndefined)(_temp)) {
                // Nothing to do, do nothing.
                return prev;
            }
            // duplicate last layer
            const currentLayer = Object.assign({}, _temp);
            // pop last modal
            currentLayer.modals.pop();
            // if current layer is empty we can remove it
            if (currentLayer.modals.length === 0) {
                return prev.slice(0, -1);
            }
            // remove last layer from original slice and add our new layer
            return [...prev.slice(0, -1), currentLayer];
        });
    }, [setLayers]);
    const changeLayerMode = react_1.default.useCallback((mode) => {
        setLayers((prev) => {
            // duplicate current layer
            const _temp = prev.at(-1);
            if ((0, is_defined_1.isUndefined)(_temp)) {
                // nothing to do, do nothing
                return prev;
            }
            const layer = Object.assign({}, _temp);
            layer.mode = mode;
            return [...prev.slice(0, -1), layer];
        });
    }, [setLayers]);
    const closeModal = react_1.default.useCallback((uuid) => {
        setLayers((prev) => {
            const _layerIndex = prev.findIndex((l) => l.modals.some((m) => m.uuid === uuid));
            if (_layerIndex < 0) {
                // nothing to do, do nothing
                return prev;
            }
            const layer = Object.assign({}, prev.at(_layerIndex));
            const modalIndex = layer.modals.findIndex((m) => m.uuid === uuid);
            layer.modals = [
                ...layer.modals.slice(0, modalIndex),
                ...layer.modals.slice(modalIndex + 1, undefined),
            ];
            // if new modals length is 0 we can remove current layer
            if (layer.modals.length === 0) {
                return [
                    ...prev.slice(0, _layerIndex),
                    ...prev.slice(_layerIndex + 1, undefined),
                ];
            }
            // if everything else is ok we can just insert our updated layer at its position and generate new layers array
            return [
                ...prev.slice(0, _layerIndex),
                layer,
                ...prev.slice(_layerIndex + 1, undefined),
            ];
        });
    }, [setLayers]);
    const currentLayer = react_1.default.useMemo(() => layers.at(-1), [layers]);
    return (react_1.default.createElement(exports.modalContext.Provider, { value: {
            createModal,
            popModal,
            addModal,
            replaceModal,
            createLayer,
            popLayer,
            changeLayerMode,
            closeModal,
            layers,
            currentLayer,
        } }, children));
};
exports.ModalProvider = ModalProvider;
